import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureAvailability } from '../../hooks/useFeatureAvailability';
import { useFeatureFlagContext } from '../../services/featureFlags/featureFlagContext';
import { Tooltip, Box } from '@mui/material';

import { Icon } from '@liftango/ui-icons';
interface FeatureGateProps {
  featureName: string;
  children: ReactNode;
  fallback?: ReactNode;
  showTooltip?: boolean;
  showLockPlaceholder?: boolean;
}

/**
 * Component that conditionally renders children based on feature availability.
 * Both checks must pass:
 *   1. API-key availability (env var detection via useFeatureAvailability)
 *   2. Per-user feature flag (admin-controlled via FeatureFlagContext)
 */
export function FeatureGate(props: FeatureGateProps) {
  const { t } = useTranslation('common');
  const {
    featureName,
    children,
    fallback,
    showTooltip = true,
    showLockPlaceholder = false,
  } = props;
  const { isAvailable, reason } = useFeatureAvailability(featureName);
  const { isFeatureRuntimeEnabled, getBlockingGroups } = useFeatureFlagContext();
  const flagEnabled = isFeatureRuntimeEnabled(featureName);
  const blockingGroups = getBlockingGroups(featureName);
  const hasExplicitFallback = Object.prototype.hasOwnProperty.call(props, 'fallback');

  if (isAvailable && flagEnabled) {
    return <>{children}</>;
  }

  // If fallback is explicitly provided (including null), render it.
  if (hasExplicitFallback) {
    return <>{fallback}</>;
  }

  // Default blocked behavior is to hide content entirely.
  if (!showLockPlaceholder) {
    return null;
  }

  // Optional: render a disabled lock placeholder (never render real children when blocked).
  const runtimeReason =
    blockingGroups.length > 0
      ? t('featureGate.blockedByGroup', { groups: blockingGroups.join(', ') })
      : t('featureGate.blockedByRuntime');
  const tooltipReason = reason || runtimeReason;

  const disabledContent = (
    <Box
      data-testid="feature-gate-lock-placeholder"
      sx={{
        position: 'relative',
        opacity: 0.8,
        cursor: 'not-allowed',
        display: 'inline-block',
        minHeight: 28,
        minWidth: 28,
        width: '100%',
        borderRadius: 1,
        border: '1px dashed',
        borderColor: 'divider',
        p: 0.5,
        backgroundColor: 'action.hover',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Icon size={20} name="lock" />
      </Box>
    </Box>
  );

  if (showTooltip && tooltipReason) {
    return (
      <Tooltip title={tooltipReason} arrow>
        {disabledContent}
      </Tooltip>
    );
  }

  return disabledContent;
}

